import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Cropper from 'react-easy-crop';
import 'react-easy-crop/react-easy-crop.css';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  Building2,
  Camera,
  ChevronDown,
  CloudSun,
  Crop,
  Eye,
  ImageIcon,
  MessageSquareText,
  Palette,
  SlidersHorizontal,
  Sparkles,
  Square,
  RectangleHorizontal,
  RectangleVertical,
  Upload,
} from 'lucide-react';
import { collection, deleteField, doc, updateDoc } from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import DesignAreaModalActions from '../components/DesignAreaModalActions';
import StyledRangeSlider from '../components/StyledRangeSlider';
import { useDesignAreaCropSnapshot } from '../hooks/useDesignAreaCropSnapshot';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useIsInsideAppLayout } from '../components/AppLayoutContext';
import DesignHexColorSection from '../components/DesignHexColorSection';
import NewDesignSelect from '../components/NewDesignSelect';
import VerticalAiBeforeAfterSlider from '../components/VerticalAiBeforeAfterSlider';
import Seo from '../seo/Seo';
import { buildPublicToolPageSchemas } from '../seo/structuredData';
import { useAuth } from '../context/AuthContext';
import { useCredits } from '../hooks/useCredits';
import { db, storage } from '../firebase';
import DesignOutputWorkspace from '../components/designWorkspace/DesignOutputWorkspace';
import DesignResultViewer from '../components/designWorkspace/DesignResultViewer';
import ToolSeoSections from '../components/ToolSeoSections';
import CollapsibleSeoContent from '../components/seo/CollapsibleSeoContent';
import { getSeoContent, getSeoFaqSchema } from '../seo/seoContentData';
import { useDesignWorkspaceMode } from '../hooks/useDesignWorkspaceMode';
import { API_BASE_URL, HAS_API_URL } from '../config/api';
import { fetchProjectDoc } from '../lib/fetchProjectDoc';
import { fetchProjectOriginalBlob } from '../lib/fetchProjectOriginalImage';
import { requestGenerateDesign } from '../lib/requestGenerateDesign';
import { downloadUrlAsFile } from '../lib/downloadUrlAsFile';
import { resolveEditSourcePreviewUrl } from '../lib/projectWorkspaceUtils';
import { trackEvent } from '../utils/analytics';
import { exportCroppedRoomJpeg } from '../lib/exportRoomCrop';
import {
  CROP_MODES,
  CROP_MODE_KEYS,
  defaultCropModeFromDimensions,
} from '../lib/roomCropModes';
import {
  EXTERIOR_DESIGN_STYLES,
  EXTERIOR_DESIGN_STYLE_DEFAULT,
  EXTERIOR_CHANGE_LEVELS,
  EXTERIOR_CHANGE_LEVEL_DEFAULT,
  FACADE_MATERIALS,
  FACADE_MATERIAL_DEFAULT,
  TIME_OF_DAY_OPTIONS,
  TIME_OF_DAY_DEFAULT,
  WEATHER_OPTIONS,
  WEATHER_DEFAULT,
  findExteriorStyleByLabel,
} from '../lib/buildingExteriorDesignOptions';
import exteriorOldPreview from '../assets/design_tools/exterior_old_H.webp';
import exteriorNewPreview from '../assets/design_tools/exterior_new_H.webp';

const CROP_MODE_ICONS = {
  square: Square,
  landscape: RectangleHorizontal,
  portrait: RectangleVertical,
};

const WORKSPACE_PREVIEW = {
  alt: 'Exterior renovation preview',
  hint: 'Try facade styles, finishes, and curb appeal ideas while preserving the building structure.',
  beforeImage: exteriorOldPreview,
  afterImage: exteriorNewPreview,
};

const NOTES_MAX = 250;
const MAX_CUSTOM_COLORS = 3;
const GENERATED_IMAGE_QUALITY_STANDARD = 'standard';
const GENERATED_IMAGE_QUALITY_HIGH = 'high';
const DEFAULT_GENERATED_IMAGE_QUALITY = GENERATED_IMAGE_QUALITY_STANDARD;
const GENERATED_IMAGE_QUALITY_OPTIONS = [
  { value: GENERATED_IMAGE_QUALITY_STANDARD, label: 'Standard image quality', cost: 1 },
  { value: GENERATED_IMAGE_QUALITY_HIGH, label: 'High image quality', cost: 3 },
];
const GENERATED_IMAGE_QUALITY_DISPLAY = {
  [GENERATED_IMAGE_QUALITY_STANDARD]: 'Standard',
  [GENERATED_IMAGE_QUALITY_HIGH]: 'High Quality',
};

/** @returns {'square'|'landscape'|'portrait'|null} */
function coerceCropModeFromDoc(raw) {
  if (typeof raw !== 'string') return null;
  const key = raw.trim();
  return CROP_MODE_KEYS.includes(key) ? key : null;
}

function GenerateWaitingIndicator({ submitPhase }) {
  return (
    <div className="generate-waiting-indicator" role="status" aria-live="polite">
      <div className="generate-waiting-text">
        {submitPhase === 'generate'
          ? 'Generating your exterior design… please wait'
          : 'Preparing generation…'}
      </div>
      <div className="generate-progress-track" aria-hidden="true">
        <div className="generate-progress-fill" />
      </div>
    </div>
  );
}

function ExteriorGenerateReviewSummary({
  selectedStyle,
  exteriorChangeLevel,
  facadeMaterial,
  timeOfDay,
  weather,
  generatedImageQuality,
  generatedImageCreditCost,
}) {
  return (
    <dl className="new-design-review-summary">
      <div className="new-design-review-summary-row">
        <dt>Exterior style</dt>
        <dd>{selectedStyle}</dd>
      </div>
      <div className="new-design-review-summary-row">
        <dt>Exterior change level</dt>
        <dd>{exteriorChangeLevel}</dd>
      </div>
      <div className="new-design-review-summary-row">
        <dt>Facade material</dt>
        <dd>{facadeMaterial}</dd>
      </div>
      {timeOfDay ? (
        <div className="new-design-review-summary-row">
          <dt>Time</dt>
          <dd>{timeOfDay}</dd>
        </div>
      ) : null}
      {weather ? (
        <div className="new-design-review-summary-row">
          <dt>Weather</dt>
          <dd>{weather}</dd>
        </div>
      ) : null}
      <div className="new-design-review-summary-row">
        <dt>Image quality</dt>
        <dd>
          {GENERATED_IMAGE_QUALITY_DISPLAY[generatedImageQuality] ??
            GENERATED_IMAGE_QUALITY_DISPLAY[GENERATED_IMAGE_QUALITY_STANDARD]}
        </dd>
      </div>
      <div className="new-design-review-summary-row">
        <dt>Credit cost</dt>
        <dd>
          {generatedImageCreditCost}{' '}
          {generatedImageCreditCost === 1 ? 'Credit' : 'Credits'}
        </dd>
      </div>
    </dl>
  );
}

function BuildingExteriorDesignPage({ publicSeo = null }) {
  const isInsideAppLayout = useIsInsideAppLayout();
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const cropPixelsRef = useRef(null);
  const croppedPreviewGenRef = useRef(0);
  const initialCropModeOverrideRef = useRef(null);
  const editHydrationProjectIdRef = useRef(null);
  const { user } = useAuth();
  const credits = useCredits();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editFromUrl = useMemo(() => {
    const raw = searchParams.get('edit');
    return typeof raw === 'string' && raw.trim() !== '' ? raw.trim() : null;
  }, [searchParams]);

  const {
    pageMode,
    resultViewMode,
    setResultViewMode,
    currentProject,
    setCurrentProject,
    enterOutputMode,
    enterEditMode,
    resetToDesign,
    isEditMode,
  } = useDesignWorkspaceMode();
  const [hydratedEditId, setHydratedEditId] = useState(null);
  const [editHydration, setEditHydration] = useState(
    () => (searchParams.get('edit')?.trim() ? 'loading' : 'idle'),
  );
  const [downloadError, setDownloadError] = useState('');
  const [zoomedImage, setZoomedImage] = useState(
    () => null,
  );

  const [selectedStyle, setSelectedStyle] = useState(EXTERIOR_DESIGN_STYLE_DEFAULT.label);
  const [exteriorChangeLevel, setExteriorChangeLevel] = useState(() => {
    const param = searchParams.get('exteriorChange');
    return EXTERIOR_CHANGE_LEVELS.includes(param) ? param : EXTERIOR_CHANGE_LEVEL_DEFAULT;
  });
  const [facadeMaterial, setFacadeMaterial] = useState(FACADE_MATERIAL_DEFAULT);
  const [timeOfDay, setTimeOfDay] = useState(TIME_OF_DAY_DEFAULT);
  const [weather, setWeather] = useState(WEATHER_DEFAULT);
  const [useAiColorTheme, setUseAiColorTheme] = useState(true);
  const [selectedColors, setSelectedColors] = useState([]);
  const [themeColorSource, setThemeColorSource] = useState('ai');
  const [colorsSectionDirty, setColorsSectionDirty] = useState(false);
  const [notes, setNotes] = useState('');
  const [generatedImageQuality, setGeneratedImageQuality] = useState(DEFAULT_GENERATED_IMAGE_QUALITY);
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [croppedPreviewUrl, setCroppedPreviewUrl] = useState('');
  const [naturalWidth, setNaturalWidth] = useState(0);
  const [naturalHeight, setNaturalHeight] = useState(0);
  const [cropMode, setCropMode] = useState('square');
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [hasValidCrop, setHasValidCrop] = useState(false);
  const [cropPopupOpen, setCropPopupOpen] = useState(false);
  const cropPopupOpenRef = useRef(cropPopupOpen);
  cropPopupOpenRef.current = cropPopupOpen;
  const [reviewPopupOpen, setReviewPopupOpen] = useState(false);
  const [exteriorColorsPopupOpen, setExteriorColorsPopupOpen] = useState(false);
  const [exteriorStylePopupOpen, setExteriorStylePopupOpen] = useState(false);
  const [submitPhase, setSubmitPhase] = useState('');
  const [error, setError] = useState('');
  const [creditsError, setCreditsError] = useState(false);
  const exteriorColorSectionRef = useRef(null);
  const selectedStyleRef = useRef(selectedStyle);
  const exteriorChangeLevelRef = useRef(exteriorChangeLevel);
  selectedStyleRef.current = selectedStyle;
  exteriorChangeLevelRef.current = exteriorChangeLevel;

  const submitting = submitPhase !== '';
  const cropConfig = CROP_MODES[cropMode] || CROP_MODES.square;
  const generatedImageCreditCost =
    generatedImageQuality === GENERATED_IMAGE_QUALITY_HIGH ? 3 : 1;
  const hasInsufficientCredits =
    credits !== null && credits < generatedImageCreditCost;
  const editSourcePreviewUrl = resolveEditSourcePreviewUrl({
    croppedPreviewUrl,
    previewUrl,
    project: currentProject,
  });
  const showWorkspaceResultPanel = Boolean(isEditMode && currentProject);
  const showWorkspaceToolGuide =
    !showWorkspaceResultPanel && !editSourcePreviewUrl && !submitting;

  const resolveGeneratedProject = useCallback(
    async (projectId, gen, draftExtras) => {
      const fromDb = await fetchProjectDoc(projectId);
      if (fromDb) return fromDb;
      if (gen.project && typeof gen.project === 'object') {
        return { id: projectId, ...gen.project };
      }
      return {
        id: projectId,
        generatedImageUrl: gen.generatedImageUrl || '',
        status: 'completed',
        toolType: 'building_exterior_design',
        designTool: 'building-exterior',
        ...draftExtras,
      };
    },
    [],
  );

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  useEffect(() => {
    return () => {
      if (croppedPreviewUrl) URL.revokeObjectURL(croppedPreviewUrl);
    };
  }, [croppedPreviewUrl]);

  const refreshCroppedPreview = useCallback(async () => {
    const pixels = cropPixelsRef.current;
    if (!previewUrl || !pixels?.width || !pixels?.height) {
      setCroppedPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return '';
      });
      return;
    }
    const gen = ++croppedPreviewGenRef.current;
    try {
      const cfg = CROP_MODES[cropMode] || CROP_MODES.square;
      const blob = await exportCroppedRoomJpeg(
        previewUrl,
        pixels,
        cfg.outWidth,
        cfg.outHeight,
        0.82,
      );
      if (gen !== croppedPreviewGenRef.current) return;
      const url = URL.createObjectURL(blob);
      setCroppedPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return url;
      });
    } catch {
      if (gen !== croppedPreviewGenRef.current) return;
      setCroppedPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return '';
      });
    }
  }, [previewUrl, cropMode]);

  const handleCropComplete = useCallback(
    (_area, areaPixels) => {
      cropPixelsRef.current = areaPixels;
      const valid = Boolean(areaPixels?.width > 0 && areaPixels?.height > 0);
      setHasValidCrop(valid);
      if (valid) {
        void refreshCroppedPreview();
      } else {
        setCroppedPreviewUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return '';
        });
      }
    },
    [refreshCroppedPreview],
  );

  const {
    openCropModal,
    setCropModalSnapshot,
    cancelCropModal,
    clearSnapshot,
  } = useDesignAreaCropSnapshot({
    cropMode,
    crop,
    zoom,
    hasValidCrop,
    croppedPreviewUrl,
    cropPixelsRef,
    setCropMode,
    setCrop,
    setZoom,
    setHasValidCrop,
    setCroppedPreviewUrl,
    setCropPopupOpen,
  });

  const closeCropPopup = useCallback(() => {
    const pixels = cropPixelsRef.current;
    if (pixels?.width > 0 && pixels?.height > 0) {
      setHasValidCrop(true);
      void refreshCroppedPreview();
    }
    clearSnapshot();
    setCropPopupOpen(false);
  }, [clearSnapshot, refreshCroppedPreview]);

  const handleDimensionLoad = useCallback((event) => {
    const img = event.currentTarget;
    const w = img.naturalWidth;
    const h = img.naturalHeight;
    setNaturalWidth(w);
    setNaturalHeight(h);
    const overrideMode = initialCropModeOverrideRef.current;
    initialCropModeOverrideRef.current = null;
    const nextMode =
      overrideMode &&
      CROP_MODES[overrideMode] &&
      CROP_MODE_KEYS.includes(overrideMode)
        ? overrideMode
        : defaultCropModeFromDimensions(w, h);
    setCropMode(nextMode);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setHasValidCrop(false);
    cropPixelsRef.current = null;
    croppedPreviewGenRef.current += 1;
    setCroppedPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return '';
    });
    if (cropPopupOpenRef.current) {
      setCropModalSnapshot({
        cropMode: nextMode,
        crop: { x: 0, y: 0 },
        zoom: 1,
        cropPixels: null,
        hasValidCrop: false,
        croppedPreviewUrl: '',
      });
    }
  }, [setCropModalSnapshot]);

  const handleCropModeChange = useCallback((mode) => {
    setCropMode(mode);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setHasValidCrop(false);
    cropPixelsRef.current = null;
    croppedPreviewGenRef.current += 1;
    setCroppedPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return '';
    });
  }, []);

  const applyUploadedImageFile = useCallback((nextFile, { openCropPopup = true } = {}) => {
    if (!nextFile || !nextFile.type.startsWith('image/')) return;
    setError('');
    setNaturalWidth(0);
    setNaturalHeight(0);
    setHasValidCrop(false);
    cropPixelsRef.current = null;
    croppedPreviewGenRef.current += 1;
    setCroppedPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return '';
    });
    setPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(nextFile);
    });
    setFile(nextFile);
    if (openCropPopup) {
      setCropPopupOpen(true);
    }
  }, []);

  const handleFileChange = useCallback((nextFile) => {
    applyUploadedImageFile(nextFile, { openCropPopup: true });
    trackEvent('exterior_image_uploaded', {
      design_tool: 'building-exterior',
      design_style: selectedStyleRef.current,
      exterior_change_level: exteriorChangeLevelRef.current,
    });
  }, [applyUploadedImageFile]);

  const handleInputChange = useCallback(
    (event) => {
      const nextFile = event.target.files?.[0];
      if (nextFile) handleFileChange(nextFile);
      event.target.value = '';
    },
    [handleFileChange],
  );

  const handleDrop = useCallback(
    (event) => {
      event.preventDefault();
      const nextFile = event.dataTransfer.files?.[0];
      if (nextFile) handleFileChange(nextFile);
    },
    [handleFileChange],
  );

  const handleNotesChange = useCallback((e) => {
    setNotes(e.target.value.slice(0, NOTES_MAX));
  }, []);

  const handleThemeSourceChange = useCallback((src) => {
    setThemeColorSource(src);
    setColorsSectionDirty(true);
    if (src === 'ai') {
      setUseAiColorTheme(true);
      setSelectedColors([]);
    } else {
      setUseAiColorTheme(false);
    }
  }, []);

  const handleThemeColorsChange = useCallback((next) => {
    setSelectedColors(next);
    if (next.length > 0) setUseAiColorTheme(false);
  }, []);

  const closeExteriorColorsPopup = useCallback(() => {
    exteriorColorSectionRef.current?.flushPendingAdd();
    setExteriorColorsPopupOpen(false);
  }, []);

  const openExteriorColorsPopup = useCallback(() => {
    setReviewPopupOpen(false);
    setExteriorStylePopupOpen(false);
    setExteriorColorsPopupOpen(true);
  }, []);

  const closeExteriorStylePopup = useCallback(() => {
    setExteriorStylePopupOpen(false);
  }, []);

  const openExteriorStylePopup = useCallback(() => {
    setReviewPopupOpen(false);
    setExteriorColorsPopupOpen(false);
    setExteriorStylePopupOpen(true);
  }, []);

  const exteriorColorButtonCaption = useMemo(() => {
    if (themeColorSource === 'custom' && selectedColors.length > 0) return null;
    if (themeColorSource === 'ai') return 'AI Choice';
    return 'Add custom colors';
  }, [themeColorSource, selectedColors.length]);

  const applyProjectFormFields = useCallback((data) => {
    setSelectedStyle(
      findExteriorStyleByLabel(data.style)?.label ??
        (typeof data.style === 'string' && data.style.trim()
          ? data.style.trim()
          : EXTERIOR_DESIGN_STYLE_DEFAULT.label),
    );
    setExteriorChangeLevel(
      EXTERIOR_CHANGE_LEVELS.includes(data.exteriorChangeLevel)
        ? data.exteriorChangeLevel
        : EXTERIOR_CHANGE_LEVEL_DEFAULT,
    );
    setFacadeMaterial(
      FACADE_MATERIALS.includes(data.facadeMaterial)
        ? data.facadeMaterial
        : FACADE_MATERIAL_DEFAULT,
    );
    setTimeOfDay(
      TIME_OF_DAY_OPTIONS.includes(data.timeOfDay) ? data.timeOfDay : TIME_OF_DAY_DEFAULT,
    );
    setWeather(
      WEATHER_OPTIONS.includes(data.weather) ? data.weather : WEATHER_DEFAULT,
    );
    setNotes(typeof data.notes === 'string' ? data.notes.trim().slice(0, NOTES_MAX) : '');
    const loadedColors = Array.isArray(data.selectedColors)
      ? data.selectedColors.filter((c) => typeof c === 'string').slice(0, MAX_CUSTOM_COLORS)
      : [];
    if (loadedColors.length > 0) {
      setSelectedColors(loadedColors);
      setUseAiColorTheme(false);
      setThemeColorSource('custom');
    } else {
      setSelectedColors([]);
      setUseAiColorTheme(data.useAiColorTheme !== false);
      setThemeColorSource(data.useAiColorTheme !== false ? 'ai' : 'custom');
    }
    const savedQuality =
      typeof data.imageQuality === 'string' ? data.imageQuality.trim().toLowerCase() : '';
    if (savedQuality === GENERATED_IMAGE_QUALITY_HIGH) {
      setGeneratedImageQuality(GENERATED_IMAGE_QUALITY_HIGH);
    } else if (savedQuality === GENERATED_IMAGE_QUALITY_STANDARD) {
      setGeneratedImageQuality(GENERATED_IMAGE_QUALITY_STANDARD);
    }
    initialCropModeOverrideRef.current = coerceCropModeFromDoc(data.cropMode);
  }, []);

  const hydrateProjectForEditing = useCallback(
    async (projectId, existingData, { redirectOnError = false } = {}) => {
      if (!user?.uid) return;
      setEditHydration('loading');
      try {
        const fromDb = await fetchProjectDoc(projectId);
        const data =
          fromDb ??
          (existingData && typeof existingData === 'object' ? existingData : null);
        if (!data) {
          throw new Error('Project not found.');
        }
        if (data.userId !== user.uid) {
          throw new Error('You do not have access to this project.');
        }
        const originalUrl =
          typeof data.originalImageUrl === 'string' ? data.originalImageUrl.trim() : '';
        if (!originalUrl) {
          throw new Error('Project is missing the original image.');
        }

        const projectOriginalApiPath = `/api/projects/${encodeURIComponent(projectId)}/original`;
        const projectOriginalApiUrl =
          HAS_API_URL && API_BASE_URL
            ? process.env.NODE_ENV === 'development'
              ? projectOriginalApiPath
              : `${API_BASE_URL}${projectOriginalApiPath}`
            : null;

        const blob = await fetchProjectOriginalBlob({
          user,
          projectId,
          projectData: data,
          fallbackUrl: originalUrl,
          projectOriginalApiUrl,
        });

        if (!blob.size) throw new Error('Project image was empty.');
        const type =
          blob.type && blob.type.startsWith('image/') ? blob.type : 'image/jpeg';
        const ext = type.includes('png') ? '.png' : '.jpg';
        const nextFile = new File([blob], `building-exterior${ext}`, { type });

        applyProjectFormFields(data);
        setCurrentProject({ id: projectId, ...data });
        applyUploadedImageFile(nextFile, { openCropPopup: true });
        setHydratedEditId(projectId);
        setEditHydration('ready');
      } catch (err) {
        setError(err?.message || 'Could not load this project for editing.');
        setEditHydration('idle');
        if (redirectOnError) {
          navigate('/projects', { replace: true });
        }
      }
    },
    [user, navigate, applyUploadedImageFile, applyProjectFormFields, setCurrentProject],
  );

  const handleOutputEdit = useCallback(() => {
    const projectId = currentProject?.id ? String(currentProject.id) : null;
    setResultViewMode('new');
    enterEditMode();
    if (!projectId) return;
    setHydratedEditId(projectId);
    void hydrateProjectForEditing(projectId, currentProject);
  }, [currentProject, enterEditMode, hydrateProjectForEditing, setResultViewMode]);

  useEffect(() => {
    if (!editFromUrl) {
      editHydrationProjectIdRef.current = null;
      setEditHydration('idle');
      initialCropModeOverrideRef.current = null;
      return undefined;
    }
    if (editHydrationProjectIdRef.current === editFromUrl) {
      return undefined;
    }
    if (!user?.uid) return undefined;
    let cancelled = false;

    async function hydrateEditProject() {
      setHydratedEditId(null);
      await hydrateProjectForEditing(editFromUrl, null, { redirectOnError: true });
      if (cancelled) return;
      editHydrationProjectIdRef.current = editFromUrl;
    }

    void hydrateEditProject();
    return () => {
      cancelled = true;
    };
  }, [editFromUrl, user, hydrateProjectForEditing]);

  useEffect(() => {
    if (
      hydratedEditId &&
      previewUrl &&
      !hasValidCrop &&
      !cropPopupOpen &&
      editHydration === 'ready' &&
      pageMode === 'design'
    ) {
      setCropPopupOpen(true);
    }
  }, [
    hydratedEditId,
    previewUrl,
    hasValidCrop,
    cropPopupOpen,
    editHydration,
    pageMode,
  ]);

  const handleSave = async () => {
    if (!user?.uid) {
      navigate(`/login?redirect=${encodeURIComponent(`${location.pathname}${location.search}`)}`);
      return;
    }
    if (!file) {
      setError('Please choose a building exterior photo first.');
      return;
    }
    const pixels = cropPixelsRef.current;
    if (!pixels || !previewUrl || !hasValidCrop) {
      setError('Adjust the crop under your uploaded photo, then try again.');
      return;
    }
    if (submitting) return;

    setError('');
    setCreditsError(false);
    setReviewPopupOpen(false);
    setSubmitPhase('upload');

    trackEvent('design_generation_started', {
      design_tool: 'building-exterior',
      design_style: selectedStyle,
      exterior_change_level: exteriorChangeLevel,
      image_quality: generatedImageQuality,
    });

    try {
      const cfg = CROP_MODES[cropMode] || CROP_MODES.square;
      const croppedBlob = await exportCroppedRoomJpeg(
        previewUrl,
        pixels,
        cfg.outWidth,
        cfg.outHeight,
      );
      const croppedFile = new File([croppedBlob], 'building-exterior.jpg', {
        type: 'image/jpeg',
      });

      const colorThemeForDoc =
        selectedColors.length > 0 ? 'custom' : colorsSectionDirty ? 'AI choice' : 'AI choice';
      const useAiForDoc = selectedColors.length === 0 && useAiColorTheme;
      const selectedColorsForDoc = selectedColors.slice(0, MAX_CUSTOM_COLORS);

      if (!HAS_API_URL) {
        setError(
          'The design server is not configured. Set REACT_APP_API_URL in your environment to generate and save projects.',
        );
        return;
      }

      const projectId = doc(collection(db, 'projects')).id;
      const storagePathNew = `users/${user.uid}/projects/${projectId}/original`;
      const storageRef = ref(storage, storagePathNew);

      await uploadBytes(storageRef, croppedFile);
      const originalImageUrlNew = await getDownloadURL(storageRef);

      if (hydratedEditId) {
        const projectRef = doc(db, 'projects', hydratedEditId);
        const storagePath = `users/${user.uid}/projects/${hydratedEditId}/original`;
        const storageRef = ref(storage, storagePath);
        await uploadBytes(storageRef, croppedFile);
        const originalImageUrl = await getDownloadURL(storageRef);
        await updateDoc(projectRef, {
          style: selectedStyle,
          exteriorChangeLevel,
          facadeMaterial,
          timeOfDay,
          weather,
          colorTheme: colorThemeForDoc,
          useAiColorTheme: useAiForDoc,
          selectedColors: selectedColorsForDoc,
          notes: notes.trim().slice(0, NOTES_MAX),
          originalImageUrl,
          cropMode,
          inputOpenAiSize: cfg.openAiSize,
          generatedImageUrl: '',
          status: 'uploaded',
          errorMessage: deleteField(),
        });
        setSubmitPhase('generate');
        const gen = await requestGenerateDesign(user, hydratedEditId, {
          regenerate: true,
          imageQuality: generatedImageQuality,
        });
        if (!gen.ok) {
          if (gen.code === 'INSUFFICIENT_CREDITS') setCreditsError(true);
          else setError(gen.error);
          return;
        }
        const regenProject = await resolveGeneratedProject(hydratedEditId, gen, {
          style: selectedStyle,
          exteriorChangeLevel,
          facadeMaterial,
          timeOfDay,
          weather,
          originalImageUrl,
        });
        enterOutputMode(regenProject);
        return;
      }

      const projectDraft = {
        designTool: 'building-exterior',
        toolType: 'building_exterior_design',
        style: selectedStyle,
        exteriorChangeLevel,
        facadeMaterial,
        timeOfDay,
        weather,
        colorTheme: colorThemeForDoc,
        useAiColorTheme: useAiForDoc,
        selectedColors: selectedColorsForDoc,
        notes: notes.trim().slice(0, NOTES_MAX),
        originalImageUrl: originalImageUrlNew,
        cropMode,
        inputOpenAiSize: cfg.openAiSize,
        ...(naturalWidth ? { originalImageWidth: naturalWidth } : {}),
        ...(naturalHeight ? { originalImageHeight: naturalHeight } : {}),
      };

      setSubmitPhase('generate');
      const gen = await requestGenerateDesign(user, projectId, {
        imageQuality: generatedImageQuality,
        pendingCreate: true,
        projectDraft,
      });

      if (!gen.ok) {
        if (gen.code === 'INSUFFICIENT_CREDITS') {
          setCreditsError(true);
        } else {
          setError(gen.error);
        }
        return;
      }

      trackEvent('design_generation_completed', {
        design_tool: 'building-exterior',
        design_style: selectedStyle,
        exterior_change_level: exteriorChangeLevel,
        time_of_day: timeOfDay,
        weather,
        image_quality: generatedImageQuality,
        project_id: projectId,
      });

      const completedProject = await resolveGeneratedProject(projectId, gen, {
        ...projectDraft,
        originalImageUrl: originalImageUrlNew,
      });
      enterOutputMode(completedProject);
      setHydratedEditId(projectId);
    } catch (err) {
      setError(err?.message || 'Upload failed. Please try again.');
    } finally {
      setSubmitPhase('');
    }
  };

  useEffect(() => {
    if (!exteriorColorsPopupOpen && !reviewPopupOpen) return undefined;
    function handleEsc(event) {
      if (event.key !== 'Escape' || submitting) return;
      if (reviewPopupOpen) {
        setReviewPopupOpen(false);
        return;
      }
      closeExteriorColorsPopup();
    }
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [exteriorColorsPopupOpen, reviewPopupOpen, submitting, closeExteriorColorsPopup]);

  const exteriorLevelOptions = useMemo(
    () => EXTERIOR_CHANGE_LEVELS.map((level) => ({ value: level, label: level })),
    [],
  );

  const editRouteBlocking = editHydration === 'loading';

  return (
    <>
      <Seo
        title={publicSeo?.title || 'Exterior Renovation | MyRoomsAI'}
        description={
          publicSeo?.description ||
          'Upload a building exterior photo and transform the facade style while preserving structure, openings, and camera view.'
        }
        canonicalPath={publicSeo?.path || '/projects/ai-exterior-design'}
        noindex={!publicSeo}
        structuredData={
          publicSeo
            ? [...buildPublicToolPageSchemas(publicSeo), getSeoFaqSchema(publicSeo.path)].filter(Boolean)
            : []
        }
      />
      {!isInsideAppLayout ? <Navbar /> : null}
      <main
        className={`dashboard-page new-design-page building-exterior-design-page${
          pageMode === 'output' ? ' project-detail-page' : ''
        }`}
      >
        <div className="container">
          {error ? (
            <p className="dashboard-inline-error" role="alert">
              {error}
            </p>
          ) : null}

          {creditsError ? (
            <p className="dashboard-inline-error" role="alert">
              You do not have enough credits.{' '}
              <Link to="/credits">Buy a credit pack</Link> to continue.
            </p>
          ) : null}

          {editHydration === 'loading' ? (
            <p className="project-page-muted" role="status">
              Loading project…
            </p>
          ) : null}

          {pageMode === 'output' && currentProject ? (
            <DesignOutputWorkspace
              project={currentProject}
              resultViewMode={resultViewMode}
              onResultViewModeChange={setResultViewMode}
              onNewProject={resetToDesign}
              onEdit={handleOutputEdit}
              shopItemCount={0}
              onDownloadImage={async (url, baseName) => {
                setDownloadError('');
                try {
                  await downloadUrlAsFile(url, baseName, user);
                } catch (err) {
                  setDownloadError(err?.message || 'Download failed.');
                }
              }}
              onZoomImage={setZoomedImage}
              downloadError={downloadError}
            />
          ) : (
          <>
          <section
            className={`dashboard-grid new-design-workspace design-dual-layout${
              pageMode === 'output' ? ' output-mode' : ''
            }${isEditMode ? ' edit-mode' : ''} new-design-workspace--preview-heading`}
          >
            <article className="card new-design-setup-card">
              {!publicSeo ? (
                <div className="new-design-setup-nav tool-category-tabs">
                  <button
                    type="button"
                    className="tool-category-tab is-active new-design-back-to-tools"
                    onClick={() => navigate('/projects')}
                    aria-label="Back to design tools"
                  >
                    <ArrowLeft size={18} strokeWidth={2} aria-hidden />
                    Back to design tools
                  </button>
                </div>
              ) : null}

              <div className="new-design-upload-mobile-summary">
                <h3 className="new-design-subheading new-design-upload-section-heading">
                  <Upload
                    className="new-design-subheading-icon"
                    size={18}
                    strokeWidth={2}
                    aria-hidden
                  />
                  Upload Building Photo
                </h3>
                <input
                  ref={fileInputRef}
                  id="building-exterior-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleInputChange}
                  className="new-design-file-input"
                />
                <input
                  ref={cameraInputRef}
                  id="building-exterior-camera"
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handleInputChange}
                  className="new-design-file-input"
                />
                {!previewUrl ? (
                  <div className="new-design-upload-empty-stack">
                    <div className="new-design-upload-dropzone-wrap">
                      <label
                        htmlFor="building-exterior-upload"
                        className="upload-dropzone"
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={handleDrop}
                      >
                        <div className="upload-dropzone-inner">
                          <span className="upload-dropzone-cloud" aria-hidden="true">
                            <Upload size={38} strokeWidth={1.75} />
                          </span>
                          <span>Upload your building exterior photo</span>
                        </div>
                      </label>
                    </div>
                    <button
                      type="button"
                      className="btn btn-outline new-design-camera-action"
                      onClick={() => cameraInputRef.current?.click()}
                    >
                      <Camera size={18} strokeWidth={2} aria-hidden="true" />
                      Take Photo
                    </button>
                  </div>
                ) : (
                  <>
                    <img
                      src={previewUrl}
                      alt=""
                      onLoad={handleDimensionLoad}
                      className="new-design-dims-probe"
                      decoding="async"
                      loading="lazy"
                    />
                    <div className="new-design-upload-mobile-preview">
                      <img
                        src={previewUrl}
                        alt="Uploaded building exterior preview"
                        className="new-design-upload-mobile-preview-img"
                        decoding="async"
                      />
                    </div>
                    <div className="new-design-upload-mobile-summary-btns">
                      <div className="new-design-upload-summary-btn-row">
                        <button
                          type="button"
                          className="btn btn-ghost new-design-compact-ghost-btn new-design-upload-summary-btn"
                          onClick={() => fileInputRef.current?.click()}
                        >
                          <ImageIcon size={15} strokeWidth={2} aria-hidden="true" />
                          Change Photo
                        </button>
                        <button
                          type="button"
                          className="btn btn-ghost new-design-compact-ghost-btn new-design-upload-summary-btn"
                          onClick={openCropModal}
                        >
                          <Crop size={15} strokeWidth={2} aria-hidden="true" />
                          Change Design Area
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>

              {previewUrl && cropPopupOpen ? (
                <div
                  className="new-design-theme-popup-backdrop"
                  role="presentation"
                  onClick={cancelCropModal}
                >
                  <div
                    className="new-design-crop-popup"
                    role="dialog"
                    aria-modal="true"
                    aria-label="Select design area"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="new-design-crop-panel-head">
                      <h3 className="new-design-subheading">
                        <Crop
                          className="new-design-subheading-icon"
                          size={18}
                          strokeWidth={2}
                          aria-hidden
                        />
                        Select design area
                      </h3>
                    </div>
                    <p className="new-design-hint">
                      Zoom in/out and then drag to frame the design area.
                    </p>
                    <div className="new-design-crop-mode-row" role="group" aria-label="Crop aspect ratio">
                          {CROP_MODE_KEYS.map((key) => {
                            const CropModeIcon = CROP_MODE_ICONS[key];
                            return (
                            <button
                              key={key}
                              type="button"
                              className={`new-design-chip new-design-crop-mode-btn${
                                cropMode === key ? ' new-design-chip--selected' : ''
                              }`}
                              aria-pressed={cropMode === key}
                              aria-label={CROP_MODES[key].label}
                              title={CROP_MODES[key].label}
                              onClick={() => handleCropModeChange(key)}
                            >
                              <CropModeIcon size={17} strokeWidth={2} aria-hidden="true" />
                            </button>
                            );
                          })}
                    </div>
                    <div className="new-design-crop-stage">
                      <Cropper
                        image={previewUrl}
                        crop={crop}
                        zoom={zoom}
                        aspect={cropConfig.aspect}
                        cropShape="rect"
                        showGrid={false}
                        onCropChange={setCrop}
                        onZoomChange={setZoom}
                        onCropComplete={handleCropComplete}
                      />
                    </div>
                    <div className="new-design-crop-zoom">
                      <span className="new-design-crop-zoom-label">Zoom</span>
                      <StyledRangeSlider
                        min={1}
                        max={3}
                        step={0.02}
                        value={zoom}
                        aria-label="Crop zoom"
                        onChange={(e) => setZoom(Number(e.target.value))}
                      />
                    </div>
                    <DesignAreaModalActions
                      onCancel={cancelCropModal}
                      onDone={closeCropPopup}
                    />
                  </div>
                </div>
              ) : null}

              <div className="new-design-setup-fields">
                <div className="new-design-control-group">
                  <h3 className="new-design-subheading">
                    <Eye
                      className="new-design-subheading-icon"
                      size={18}
                      strokeWidth={2}
                      aria-hidden
                    />
                    Exterior Style
                  </h3>
                  <div
                    className={`new-design-select${
                      exteriorStylePopupOpen ? ' new-design-select--open' : ''
                    }`}
                  >
                    <button
                      type="button"
                      id="building-exterior-style"
                      className="new-design-select-trigger"
                      aria-haspopup="dialog"
                      aria-expanded={exteriorStylePopupOpen}
                      aria-controls="building-exterior-style-popup"
                      onClick={openExteriorStylePopup}
                      disabled={editRouteBlocking || submitting}
                    >
                      <span className="new-design-select-trigger-label">{selectedStyle}</span>
                      <span className="new-design-select-trigger-meta">
                        <ChevronDown
                          className="new-design-select-chevron"
                          size={14}
                          strokeWidth={2.5}
                          aria-hidden
                        />
                      </span>
                    </button>
                  </div>
                </div>

                <div className="new-design-control-group">
                  <h3 className="new-design-subheading">
                    <SlidersHorizontal
                      className="new-design-subheading-icon"
                      size={18}
                      strokeWidth={2}
                      aria-hidden
                    />
                    Exterior Change Level
                  </h3>
                  <NewDesignSelect
                    id="building-exterior-change-level"
                    value={exteriorChangeLevel}
                    onChange={setExteriorChangeLevel}
                    disabled={submitting}
                    options={exteriorLevelOptions}
                  />
                </div>

                <div className="new-design-control-group">
                  <h3 className="new-design-subheading">
                    <Building2
                      className="new-design-subheading-icon"
                      size={18}
                      strokeWidth={2}
                      aria-hidden
                    />
                    Facade Material
                  </h3>
                  <NewDesignSelect
                    id="building-exterior-facade-material"
                    value={facadeMaterial}
                    onChange={setFacadeMaterial}
                    disabled={submitting}
                    options={FACADE_MATERIALS}
                  />
                </div>
              </div>

              <div className="new-design-control-group new-design-colors-combined-group">
                <h3
                  className="new-design-subheading new-design-colors-combined-heading"
                  id="building-exterior-environment-heading"
                >
                  <CloudSun
                    className="new-design-subheading-icon"
                    size={18}
                    strokeWidth={2}
                    aria-hidden
                  />
                  Environment
                </h3>
                <div className="new-design-colors-combined-grid">
                  <div className="new-design-colors-combined-item">
                    <span
                      className="new-design-colors-combined-label"
                      id="building-exterior-time-label"
                    >
                      Time
                    </span>
                    <NewDesignSelect
                      id="building-exterior-time-of-day"
                      value={timeOfDay}
                      onChange={setTimeOfDay}
                      disabled={submitting}
                      options={TIME_OF_DAY_OPTIONS}
                      aria-labelledby="building-exterior-environment-heading building-exterior-time-label"
                    />
                  </div>
                  <div className="new-design-colors-combined-item">
                    <span
                      className="new-design-colors-combined-label"
                      id="building-exterior-weather-label"
                    >
                      Weather
                    </span>
                    <NewDesignSelect
                      id="building-exterior-weather"
                      value={weather}
                      onChange={setWeather}
                      disabled={submitting}
                      options={WEATHER_OPTIONS}
                      aria-labelledby="building-exterior-environment-heading building-exterior-weather-label"
                    />
                  </div>
                </div>
              </div>

              <div className="new-design-control-group new-design-colors-combined-group">
                <h3
                  className="new-design-subheading new-design-colors-combined-heading"
                  id="building-exterior-colors-heading"
                >
                  <Palette
                    className="new-design-subheading-icon"
                    size={18}
                    strokeWidth={2}
                    aria-hidden
                  />
                  Colors
                </h3>
                <div className="new-design-colors-combined-grid">
                  <div className="new-design-colors-combined-item">
                    <button
                      type="button"
                      className="btn btn-ghost new-design-customize-popup-trigger new-design-customize-popup-trigger--color-picker"
                      aria-haspopup="dialog"
                      aria-expanded={exteriorColorsPopupOpen}
                      aria-controls="building-exterior-colors-popup"
                      aria-labelledby="building-exterior-colors-heading"
                      onClick={openExteriorColorsPopup}
                      disabled={editRouteBlocking || submitting}
                    >
                      {themeColorSource === 'custom' && selectedColors.length > 0 ? (
                        <span
                          className="new-design-color-swatches new-design-color-swatches--in-button"
                          aria-label="Selected custom exterior colors"
                        >
                          {selectedColors.map((color) => (
                            <span
                              key={`exterior-${color}`}
                              className="new-design-color-swatch"
                              style={{ backgroundColor: color }}
                              title={color}
                              aria-label={color}
                            />
                          ))}
                        </span>
                      ) : null}
                      {exteriorColorButtonCaption ? (
                        <span className="new-design-customize-trigger-caption">
                          {exteriorColorButtonCaption}
                        </span>
                      ) : null}
                    </button>
                  </div>
                </div>
                <p className="new-design-field-helper">
                  Set exterior colors, or use AI Choice.
                </p>
              </div>

              <div className="new-design-review-notes-group">
                <h3 className="new-design-subheading">
                  <MessageSquareText
                    className="new-design-subheading-icon"
                    size={18}
                    strokeWidth={2}
                    aria-hidden
                  />
                  Notes & instructions for the AI
                </h3>
                <textarea
                  className="dashboard-notes new-design-notes"
                  rows={2}
                  maxLength={NOTES_MAX}
                  placeholder="Example: Keep the garage door white, add warmer exterior lighting."
                  value={notes}
                  onChange={handleNotesChange}
                  disabled={submitting}
                />
                <p className="new-design-char-count" aria-live="polite">
                  {notes.length}/{NOTES_MAX}
                </p>
              </div>

              <div className="new-design-control-group new-design-setup-quality-group">
                <h3 className="new-design-subheading" id="building-exterior-quality-label">
                  <ImageIcon
                    className="new-design-subheading-icon"
                    size={18}
                    strokeWidth={2}
                    aria-hidden
                  />
                  Image quality
                </h3>
                <NewDesignSelect
                  id="building-exterior-image-quality"
                  aria-labelledby="building-exterior-quality-label"
                  value={generatedImageQuality}
                  disabled={submitting}
                  onChange={setGeneratedImageQuality}
                  options={GENERATED_IMAGE_QUALITY_OPTIONS}
                />
              </div>

              <div className="new-design-setup-generate-group">
                <button
                  type="button"
                  className="btn btn-primary full-width new-design-generate-btn"
                  onClick={() => void handleSave()}
                  disabled={
                    editRouteBlocking ||
                    submitting ||
                    !file ||
                    !naturalWidth ||
                    !hasValidCrop ||
                    hasInsufficientCredits
                  }
                >
                  <Sparkles size={25} strokeWidth={2} aria-hidden="true" />
                  Generate design
                </button>
                {hasInsufficientCredits ? (
                  <div className="new-design-no-credits" role="status">
                    <span>You do not have enough credits.</span>
                    <Link to="/credits" className="btn btn-outline new-design-get-credit-btn">
                      Get credit
                    </Link>
                  </div>
                ) : null}
              </div>
            </article>

            {showWorkspaceResultPanel ? (
              <DesignResultViewer
                project={currentProject}
                editSourceImageUrl={editSourcePreviewUrl}
                resultViewMode={resultViewMode}
                onDownloadImage={async (url, baseName) => {
                  setDownloadError('');
                  try {
                    await downloadUrlAsFile(url, baseName, user);
                  } catch (err) {
                    setDownloadError(err?.message || 'Download failed.');
                  }
                }}
                onZoomImage={setZoomedImage}
                showShopOnMobile={false}
                previewLayout="plain"
              />
            ) : (
            <div
              className={`new-design-workspace-preview${
                croppedPreviewUrl ? ' new-design-workspace-preview--has-image' : ''
              }${showWorkspaceToolGuide ? ' new-design-workspace-preview--has-guide' : ''}${
                submitting ? ' new-design-workspace-preview--submitting' : ''
              }`}
              aria-label="Design preview"
            >
              <div className="new-design-workspace-preview-heading-group">
                <h1 className="tool-hub-heading">
                  {publicSeo?.h1 ? (
                    publicSeo.h1
                  ) : (
                    <>
                      Exterior <span className="new-design-page-heading-accent">Renovation</span>
                    </>
                  )}
                </h1>
                {publicSeo?.headingSubtext ? (
                  <p className="new-design-workspace-preview-heading-subtext">{publicSeo.headingSubtext}</p>
                ) : null}
                {showWorkspaceToolGuide ? (
                  <p className="new-design-workspace-preview-tool-hint">{WORKSPACE_PREVIEW.hint}</p>
                ) : null}
              </div>
              {showWorkspaceToolGuide ? (
                <div className="new-design-workspace-preview-guide">
                  <div className="new-design-workspace-preview-tool-media new-design-workspace-preview-tool-media--vertical-scan">
                    <div className="new-design-workspace-preview-offset-rectangle" aria-hidden="true" />
                    <VerticalAiBeforeAfterSlider
                      beforeImage={WORKSPACE_PREVIEW.beforeImage}
                      afterImage={WORKSPACE_PREVIEW.afterImage}
                      beforeAlt={`${WORKSPACE_PREVIEW.alt} before`}
                      afterAlt={`${WORKSPACE_PREVIEW.alt} after`}
                      fallbackSrc={WORKSPACE_PREVIEW.afterImage}
                      showLabels
                    />
                  </div>
                </div>
              ) : null}
              {croppedPreviewUrl || submitting ? (
                <div className="new-design-workspace-preview-content">
                  {croppedPreviewUrl ? (
                    <div className="new-design-workspace-preview-media">
                      <img
                        src={croppedPreviewUrl}
                        alt="Cropped building exterior preview"
                        className="new-design-workspace-preview-img"
                        decoding="async"
                      />
                    </div>
                  ) : null}
                  {submitting ? <GenerateWaitingIndicator submitPhase={submitPhase} /> : null}
                </div>
              ) : null}
            </div>
            )}
          </section>
          {downloadError ? (
            <p className="dashboard-inline-error project-detail-download-error" role="alert">
              {downloadError}
            </p>
          ) : null}
          </>
          )}
          {publicSeo ? <ToolSeoSections {...publicSeo} /> : null}
          {publicSeo ? <CollapsibleSeoContent {...getSeoContent(publicSeo.path)} /> : null}
        </div>
        {!isInsideAppLayout ? <Footer /> : null}
      </main>
      {zoomedImage ? (
        <div
          className="image-lightbox-backdrop"
          role="presentation"
          onClick={() => setZoomedImage(null)}
        >
          <div
            className="image-lightbox-content"
            role="dialog"
            aria-modal="true"
            aria-label="Image preview"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="image-lightbox-close"
              aria-label="Close image preview"
              onClick={() => setZoomedImage(null)}
            >
              Close
            </button>
            <img
              src={zoomedImage.src}
              alt={zoomedImage.alt}
              className="image-lightbox-img"
              loading="eager"
              decoding="sync"
            />
          </div>
        </div>
      ) : null}

      {exteriorStylePopupOpen ? (
        <div
          className="new-design-theme-popup-backdrop"
          role="presentation"
          onClick={closeExteriorStylePopup}
        >
          <div
            id="building-exterior-style-popup"
            className="new-design-theme-popup new-design-theme-popup--furniture new-design-theme-popup--exterior-style-picker"
            role="dialog"
            aria-modal="true"
            aria-labelledby="building-exterior-style-popup-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="new-design-theme-popup-head">
              <h2 id="building-exterior-style-popup-title" className="new-design-theme-popup-title">
                Exterior Style
              </h2>
              <button
                type="button"
                className="btn btn-ghost new-design-theme-popup-close"
                onClick={closeExteriorStylePopup}
              >
                Close
              </button>
            </div>
            <div
              className="new-design-furniture-grid exterior-style-picker-grid"
              role="group"
              aria-label="Exterior style options"
            >
              {EXTERIOR_DESIGN_STYLES.map((style) => {
                const isSelected = selectedStyle === style.label;
                return (
                  <button
                    key={style.id}
                    type="button"
                    className={`new-design-furniture-tile exterior-style-card-tile${
                      isSelected ? ' new-design-furniture-tile--active' : ''
                    }`}
                    aria-pressed={isSelected}
                    aria-label={`${style.label}. ${style.shortDescription}`}
                    onClick={() => {
                      setSelectedStyle(style.label);
                      closeExteriorStylePopup();
                    }}
                  >
                    {style.image ? (
                      <img
                        src={style.image}
                        alt=""
                        className="exterior-style-card-thumb"
                        loading="lazy"
                        decoding="async"
                        aria-hidden="true"
                      />
                    ) : (
                      <span
                        className="exterior-style-card-thumb exterior-style-card-thumb--placeholder"
                        aria-hidden="true"
                      >
                        <Building2 size={28} strokeWidth={1.75} />
                      </span>
                    )}
                    <span className="new-design-furniture-tile-label">{style.label}</span>
                    <span className="exterior-style-card-desc">{style.shortDescription}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      ) : null}

      {exteriorColorsPopupOpen ? (
        <div
          className="new-design-theme-popup-backdrop"
          role="presentation"
          onClick={closeExteriorColorsPopup}
        >
          <div
            id="building-exterior-colors-popup"
            className="new-design-theme-popup"
            role="dialog"
            aria-modal="true"
            aria-labelledby="building-exterior-colors-popup-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="new-design-theme-popup-head">
              <h2 id="building-exterior-colors-popup-title" className="new-design-theme-popup-title">
                Choose exterior colors
              </h2>
              <button
                type="button"
                className="btn btn-primary new-design-theme-popup-close"
                onClick={closeExteriorColorsPopup}
              >
                Done
              </button>
            </div>
            <DesignHexColorSection
              ref={exteriorColorSectionRef}
              source={themeColorSource}
              onSourceChange={handleThemeSourceChange}
              maxColors={MAX_CUSTOM_COLORS}
              selectedColors={selectedColors}
              onSelectedColorsChange={handleThemeColorsChange}
              onMarkTouched={() => setColorsSectionDirty(true)}
              groupAriaLabel="Exterior colors"
              aiChoiceHint="AI will choose a suitable exterior palette for your selected style."
              showAiHint={false}
            />
          </div>
        </div>
      ) : null}

      {reviewPopupOpen ? (
        <div
          className="new-design-theme-popup-backdrop"
          role="presentation"
          onClick={() => {
            if (!submitting) setReviewPopupOpen(false);
          }}
        >
          <div
            className="new-design-theme-popup new-design-theme-popup--review"
            role="dialog"
            aria-modal="true"
            aria-labelledby="building-exterior-review-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="new-design-theme-popup-head new-design-review-popup-head">
              <h2 id="building-exterior-review-title" className="new-design-theme-popup-title">
                <Sparkles size={20} strokeWidth={2.25} aria-hidden />
                Review & Generate
              </h2>
              <button
                type="button"
                className="btn btn-ghost new-design-theme-popup-close"
                disabled={submitting}
                onClick={() => setReviewPopupOpen(false)}
              >
                Cancel
              </button>
            </div>

            <ExteriorGenerateReviewSummary
              selectedStyle={selectedStyle}
              exteriorChangeLevel={exteriorChangeLevel}
              facadeMaterial={facadeMaterial}
              timeOfDay={timeOfDay}
              weather={weather}
              generatedImageQuality={generatedImageQuality}
              generatedImageCreditCost={generatedImageCreditCost}
            />

            <div className="new-design-review-actions">
              <button
                type="button"
                className="btn btn-ghost"
                disabled={submitting}
                onClick={() => setReviewPopupOpen(false)}
              >
                Back
              </button>
              <button
                type="button"
                className="btn btn-primary new-design-generate-btn"
                disabled={submitting || hasInsufficientCredits}
                onClick={() => void handleSave()}
              >
                <Sparkles size={25} strokeWidth={2} aria-hidden="true" />
                {submitPhase === 'upload'
                  ? 'Uploading…'
                  : submitPhase === 'generate'
                    ? 'Generating…'
                    : 'Generate'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

export default BuildingExteriorDesignPage;
